import {
  Combobox,
  DropdownButton,
  FormAlert
} from '@eggplant/pattern-library'
import type {
  ButtonShape,
  GetGitRefsResponse,
  GitRef,
  GroupedOptionType,
  GroupedOptions,
  OptionType,
  Options
} from '@eggplant/types'

interface Props {
  initialOptions?: GroupedOptions
  onChange: (value: string) => void
  value: string
  gitRefs: GetGitRefsResponse
  isDisabled?: boolean
  customValidity?: string
}

const mapRefToOption = (ref: GitRef) => ({
  label: ref.name,
  value: ref.ref
} as OptionType)

const getOptions = (refs: GetGitRefsResponse) => {
  const branchOptions = refs.branches.map(mapRefToOption)
  const tagOptions = refs.tags.map(mapRefToOption)

  return [{
    label: 'Branches',
    options: branchOptions
  }, {
    label: 'Tags',
    options: tagOptions
  }]
}

const groupedOptionReducer = (acc: Options, curr: GroupedOptionType) =>
  curr.options.length
    ? acc.concat(curr.options)
    : acc

const SelectGitRef = ({
  initialOptions = [],
  onChange,
  value,
  gitRefs,
  isDisabled=false,
  customValidity
}: Props) => {
  const options = getOptions(gitRefs)
  const combinedOptions = options.reduce(groupedOptionReducer, [])

  const isValid = value !== '' && combinedOptions.length > 0 && combinedOptions.some(option => option.value === value)

  const getDefaultLabel = () => {
    const defaultLabel = value === ''
      ? 'Select branch or tag'
      : 'Branch not found'

    if (!initialOptions.length) return defaultLabel

    const combinedOptions = initialOptions.reduce(groupedOptionReducer, [])

    const overridenDefaultLabel = combinedOptions.find(item => item.value === '')

    return overridenDefaultLabel?.label || defaultLabel
  }

  const getRenderButton = (options: any) => {
    const combinedOptions = options.reduce(groupedOptionReducer, [])

    const currentOption = combinedOptions.find((option: OptionType) => option.value === value)

    return (buttonProps: ButtonShape) =>
      <DropdownButton
        {...buttonProps}
        isDisabled={isDisabled}
        label={
          currentOption?.value
            ? currentOption.label
            : undefined
        }
      >
        {
          currentOption
            ? currentOption.label
            : getDefaultLabel()
        }
      </DropdownButton>
  }

  return (
    gitRefs.branches.length > 0 || gitRefs.tags.length > 0
      ? (
        <Combobox
          customValidity={customValidity}
          groupedOptions={options}
          initialGroupedOptions={initialOptions}
          isInvalid={value !== '' && !isValid}
          noOptionsPlaceholder='Branch not found'
          onChange={onChange}
          renderButton={getRenderButton(options)}
          value={value}
        />
      )
      : (
        <FormAlert type='warning'>
          No Branches or Tags found
        </FormAlert>
      )
  )
}

export default SelectGitRef

